import { trackLinkClick } from 'utils/tracking';
import {
  Page_Pagebuilder_Blocks_WowForm,
  Page_Pagebuilder_Blocks_WowForm_Steps_Text,
  Page_Pagebuilder_Blocks_WowForm_Steps_TextRules,
} from '@/graphql/generated/graphql';
import { ApiError, useContribution, useResellers } from '@/hooks/useWow';
import { revalidateLogic, useForm } from '@tanstack/react-form';
import { useEffect, useRef, useState } from 'react';
import { FieldError } from '../LaddboxForm';
import GoogleRecaptcha from './google-recaptcha';
import { ImageDropField } from './image-drop-field';
import { StepNumber } from './list';
import { type Reseller } from './map';
import { ResellerField } from './reseller-field';
import { sanitize } from './sanitize';
import {
  defaultValues,
  haversineKm,
  inputClass,
  inputErrorClass,
  labelClass,
  schema,
  type FormWOWValues,
  type SelectionMode,
} from './schema';
import { SuccessModal } from './success-modal';
import { TermsModal } from './terms-modal';
import { TiltCard } from './tilt-card';

const RECAPTCHA_KEY = process.env.GOOGLE_CLIENT_RECAPTCHA;
if (!RECAPTCHA_KEY)
  throw new Error('Missing required env var: GOOGLE_CLIENT_RECAPTCHA');

const stepRotations = ['-4deg', '3deg', '-2deg', '4deg', '-3deg'];

function WowForm({
  terms,
  successModalDescription,
  successModalTitle,
  steps,
}: Page_Pagebuilder_Blocks_WowForm) {
  const { mutateAsync } = useContribution();
  const { data: resellersData, isError: resellersError } = useResellers();
  const [selectedReseller, setSelectedReseller] = useState<Reseller | null>(
    null,
  );
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('idle');
  const [mapOpen, setMapOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const honeypotRef = useRef('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    GoogleRecaptcha();
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = '.grecaptcha-badge { visibility: hidden; }';
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const form = useForm({
    defaultValues,
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    validators: { onDynamic: schema },
    onSubmit: async ({ value }) => {
      if (honeypotRef.current) return;
      try {
        const recaptcha = await GoogleRecaptcha();
        const token = await recaptcha.enterprise.execute(RECAPTCHA_KEY, {
          action: 'submit',
        });
        await mutateAsync({ ...value, recaptcha_token: token });
        setSuccessModalOpen(true);
      } catch (error) {
        if (error instanceof ApiError) {
          const data = error.data as {
            errors?: Record<string, string[]>;
            message?: string;
          } | null;
          if (data?.errors) {
            const entries = Object.entries(data.errors);
            if (entries.every(([field]) => field === 'recaptcha_token')) {
              form.setErrorMap({
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onServer: 'Något gick fel, försök igen senare.' as any,
              });
            } else {
              for (const [field, messages] of entries) {
                if (field === 'recaptcha_token') continue;
                form.setFieldMeta(field as keyof FormWOWValues, (prev) => ({
                  ...prev,
                  errorMap: { ...prev.errorMap, onServer: messages[0] },
                }));
              }
            }
          } else {
            form.setErrorMap({
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onServer: (data?.message ??
                'Något gick fel, försök igen senare.') as any,
            });
          }
        } else {
          console.error(error);
          form.setErrorMap({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onServer: 'Något gick fel, försök igen senare.' as any,
          });
        }
      }
    },
  });

  const clearServerError = (fieldName: keyof FormWOWValues) => {
    form.setFieldMeta(fieldName, (prev) => ({
      ...prev,
      errorMap: { ...prev.errorMap, onServer: undefined },
    }));
  };

  const resellers: Reseller[] = (resellersData ?? []).filter(
    (d: Reseller) => d.lat != null && d.lon != null,
  );

  const handleResellerSelect = (reseller: Reseller | null) => {
    setSelectedReseller(reseller);
    form.setFieldValue('reseller_id', reseller?.id ?? 0);
    clearServerError('reseller_id');
    if (reseller) {
      if (
        selectionMode === 'denied' ||
        selectionMode === 'error' ||
        selectionMode === 'located'
      ) {
        setMapOpen(false);
      } else {
        setSelectionMode('located');
      }
    }
  };

  const handleGeolocate = () => {
    setSelectionMode('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (resellers.length === 0) {
          setSelectionMode('error');
          return;
        }
        const closest = resellers.reduce((best, r) =>
          haversineKm(pos.coords.latitude, pos.coords.longitude, r.lat, r.lon) <
          haversineKm(
            pos.coords.latitude,
            pos.coords.longitude,
            best.lat,
            best.lon,
          )
            ? r
            : best,
        );
        handleResellerSelect(closest);
      },
      (err) => {
        setSelectionMode(
          err.code === err.PERMISSION_DENIED ? 'denied' : 'error',
        );
        setMapOpen(true);
      },
    );
  };

  return (
    <section className="text-dark-blue  relative">
      {successModalOpen && (
        <SuccessModal
          title={successModalTitle}
          description={successModalDescription}
          onClose={() => {
            setSuccessModalOpen(false);
            form.reset();
            setSelectedReseller(null);
            setAgeConfirmed(false);
            setTermsAccepted(false);
            setSelectionMode('idle');
          }}
        />
      )}
      {termsModalOpen && (
        <TermsModal
          content={terms ?? ''}
          onAccept={() => {
            setTermsAccepted(true);
            setTermsModalOpen(false);
          }}
          onDecline={() => setTermsModalOpen(false)}
        />
      )}

      <div className="max-w-screen-kia-md mx-auto px-4 relative z-10">
        <ol className="grid gap-20">
          {(steps ?? []).filter(Boolean).map((step, i) => {
            const rotate = stepRotations[i % stepRotations.length];
            const n = String(i + 1);

            if (step.__typename?.endsWith('_Steps_Text')) {
              const s = step as Page_Pagebuilder_Blocks_WowForm_Steps_Text;
              return (
                <li
                  key={i}
                  className="grid grid-cols-[auto_1fr] gap-4 gap-y-0 items-start"
                >
                  <StepNumber n={n} rotate={rotate} />
                  <h3 className="text-2xl font-semibold leading-snug self-end">
                    {s.title}
                  </h3>
                  {s.description && (
                    <div
                      className="text-dark-blue/70 leading-relaxed col-start-1 col-span-2 md:col-start-2 md:col-span-1 mt-3 place-self-start"
                      dangerouslySetInnerHTML={{
                        __html: sanitize(s.description),
                      }}
                    />
                  )}
                  {s.textCtaUrl && (
                    <a
                      className="grid bg-dark-blue text-white border border-black hover:bg-transparent hover:text-black transition-colors py-3 no-underline place-content-center mt-8 rounded-sm col-start-1 col-span-2 md:col-start-2 md:col-span-1"
                      href={s.textCtaUrl}
                      onClick={trackLinkClick}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {s.textCtaLabel ?? s.textCtaUrl}
                    </a>
                  )}
                </li>
              );
            }

            if (step.__typename?.endsWith('_Steps_TextRules')) {
              const s = step as Page_Pagebuilder_Blocks_WowForm_Steps_TextRules;
              return (
                <li
                  key={i}
                  className="grid grid-cols-[auto_1fr] gap-4 gap-y-0 items-start"
                >
                  <StepNumber n={n} rotate={rotate} />
                  <h3 className="text-2xl font-semibold leading-snug self-end">
                    {s.title}
                  </h3>
                  {s.description && (
                    <div
                      className="text-dark-blue/70 leading-relaxed col-start-1 col-span-2 md:col-start-2 md:col-span-1 mt-3 place-self-start"
                      dangerouslySetInnerHTML={{
                        __html: sanitize(s.description),
                      }}
                    />
                  )}
                  <div className="col-start-1 col-span-2 md:col-start-2 md:col-span-1">
                    <TiltCard>
                      <div className="bg-dark-blue -mx-6 -mt-5 px-6 py-4 rounded-t-xl mb-5">
                        <p className="text-base font-bold text-white tracking-tight">
                          {s.rulesTitle}
                        </p>
                      </div>
                      <ul className="divide-y divide-dark-blue/10">
                        {(s.rulesItems ?? []).filter(Boolean).map((item, j) => (
                          <li
                            key={j}
                            className="flex gap-4 items-baseline py-3.5"
                          >
                            <span className="shrink-0 w-6 h-6 rounded-full bg-dark-blue flex items-center justify-center text-xs font-bold text-white mt-0.5">
                              {j + 1}
                            </span>
                            <span className="text-dark-blue/80 text-sm">
                              {item.text}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </TiltCard>
                    {s.ctaUrl && (
                      <a
                        className="grid bg-dark-blue text-white border border-black hover:bg-transparent hover:text-black transition-colors py-3 no-underline place-content-center mt-8 rounded-sm"
                        href={s.ctaUrl}
                        onClick={trackLinkClick}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {s.ctaLabel ?? s.ctaUrl}
                      </a>
                    )}
                  </div>
                </li>
              );
            }

            return (
              <li
                key={i}
                className="grid grid-cols-[auto_1fr] gap-4 gap-y-0 items-start"
              >
                <StepNumber n={n} rotate={rotate} />
                <h3 className="text-2xl font-semibold leading-snug self-end">
                  {step.title}
                </h3>
                {step.description && (
                  <div
                    className="text-dark-blue/70 leading-relaxed col-start-1 col-span-2 md:col-start-2 md:col-span-1 mt-3"
                    dangerouslySetInnerHTML={{
                      __html: sanitize(step.description),
                    }}
                  />
                )}
                <div className="col-start-1 col-span-2">
                  <div className="mt-4 lg:mt-7 bg-white border border-dark-blue/10 shadow-[0_12px_40px_rgba(0,0,0,0.10)] rounded-xl px-4 lg:px-6 py-6">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        form.handleSubmit();
                      }}
                    >
                      <div className="grid gap-4">
                        <div
                          aria-hidden="true"
                          className="absolute -left-[9999px] opacity-0 pointer-events-none"
                        >
                          <label htmlFor="website">Website</label>
                          <input
                            id="website"
                            name="website"
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            onChange={(e) => {
                              honeypotRef.current = e.target.value;
                            }}
                          />
                        </div>

                        <form.Field
                          name="reseller_id"
                          children={(field) => (
                            <ResellerField
                              selectionMode={selectionMode}
                              selectedReseller={selectedReseller}
                              mapOpen={mapOpen}
                              errors={field.state.meta.errors as string[]}
                              resellersAvailable={!resellersError}
                              onGeolocate={handleGeolocate}
                              onOpenMap={() => setMapOpen(true)}
                              onSelectReseller={handleResellerSelect}
                            />
                          )}
                        />

                        <div className="grid md:grid-cols-2 gap-4">
                          <form.Field
                            name="first_name"
                            children={(field) => (
                              <div>
                                <label
                                  className={labelClass}
                                  htmlFor={field.name}
                                >
                                  Förnamn
                                </label>
                                <input
                                  className={
                                    field.state.meta.errors.length
                                      ? inputErrorClass
                                      : inputClass
                                  }
                                  type="text"
                                  placeholder="Förnamn"
                                  id={field.name}
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={(e) => {
                                    field.handleChange(e.target.value);
                                    clearServerError(field.name);
                                  }}
                                />
                                <FieldError errors={field.state.meta.errors} />
                              </div>
                            )}
                          />
                          <form.Field
                            name="last_name"
                            children={(field) => (
                              <div>
                                <label
                                  className={labelClass}
                                  htmlFor={field.name}
                                >
                                  Efternamn
                                </label>
                                <input
                                  className={
                                    field.state.meta.errors.length
                                      ? inputErrorClass
                                      : inputClass
                                  }
                                  type="text"
                                  placeholder="Efternamn"
                                  id={field.name}
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={(e) => {
                                    field.handleChange(e.target.value);
                                    clearServerError(field.name);
                                  }}
                                />
                                <FieldError errors={field.state.meta.errors} />
                              </div>
                            )}
                          />
                        </div>

                        <form.Field
                          name="address"
                          children={(field) => (
                            <div>
                              <label
                                className={labelClass}
                                htmlFor={field.name}
                              >
                                Gatuadress
                              </label>
                              <input
                                className={
                                  field.state.meta.errors.length
                                    ? inputErrorClass
                                    : inputClass
                                }
                                type="text"
                                placeholder="Gatuadress"
                                id={field.name}
                                value={field.state.value}
                                onBlur={field.handleBlur}
                                onChange={(e) => {
                                  field.handleChange(e.target.value);
                                  clearServerError(field.name);
                                }}
                              />
                              <FieldError errors={field.state.meta.errors} />
                            </div>
                          )}
                        />

                        <div className="grid md:grid-cols-2 gap-4">
                          <form.Field
                            name="zip"
                            children={(field) => (
                              <div>
                                <label
                                  className={labelClass}
                                  htmlFor={field.name}
                                >
                                  Postnummer
                                </label>
                                <input
                                  className={
                                    field.state.meta.errors.length
                                      ? inputErrorClass
                                      : inputClass
                                  }
                                  type="text"
                                  placeholder="12345"
                                  id={field.name}
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={(e) => {
                                    field.handleChange(e.target.value);
                                    clearServerError(field.name);
                                  }}
                                />
                                <FieldError errors={field.state.meta.errors} />
                              </div>
                            )}
                          />
                          <form.Field
                            name="city"
                            children={(field) => (
                              <div>
                                <label
                                  className={labelClass}
                                  htmlFor={field.name}
                                >
                                  Postort
                                </label>
                                <input
                                  className={
                                    field.state.meta.errors.length
                                      ? inputErrorClass
                                      : inputClass
                                  }
                                  type="text"
                                  placeholder="Postort"
                                  id={field.name}
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={(e) => {
                                    field.handleChange(e.target.value);
                                    clearServerError(field.name);
                                  }}
                                />
                                <FieldError errors={field.state.meta.errors} />
                              </div>
                            )}
                          />
                        </div>

                        <form.Field
                          name="phone"
                          children={(field) => (
                            <div>
                              <label
                                className={labelClass}
                                htmlFor={field.name}
                              >
                                Mobiltelefonnummer
                              </label>
                              <input
                                className={
                                  field.state.meta.errors.length
                                    ? inputErrorClass
                                    : inputClass
                                }
                                type="tel"
                                placeholder="070 123 45 67"
                                id={field.name}
                                value={field.state.value}
                                onBlur={field.handleBlur}
                                onChange={(e) => {
                                  field.handleChange(e.target.value);
                                  clearServerError(field.name);
                                }}
                              />
                              <FieldError errors={field.state.meta.errors} />
                            </div>
                          )}
                        />

                        <form.Field
                          name="email"
                          children={(field) => (
                            <div>
                              <label
                                className={labelClass}
                                htmlFor={field.name}
                              >
                                E-postadress
                              </label>
                              <input
                                className={
                                  field.state.meta.errors.length
                                    ? inputErrorClass
                                    : inputClass
                                }
                                type="email"
                                placeholder="din@epost.se"
                                id={field.name}
                                value={field.state.value}
                                onBlur={field.handleBlur}
                                onChange={(e) => {
                                  field.handleChange(e.target.value);
                                  clearServerError(field.name);
                                }}
                              />
                              <FieldError errors={field.state.meta.errors} />
                            </div>
                          )}
                        />

                        <form.Field
                          name="image"
                          children={(field) => (
                            <ImageDropField
                              name={field.name}
                              value={field.state.value as File | undefined}
                              errors={field.state.meta.errors as string[]}
                              onBlur={field.handleBlur}
                              onChange={(file) => {
                                field.handleChange(file as File);
                                clearServerError(field.name);
                              }}
                            />
                          )}
                        />
                      </div>

                      <div className="mt-4 flex flex-col gap-3">
                        <div className="flex items-start gap-3 text-sm">
                          <input
                            type="checkbox"
                            id="terms-checkbox"
                            aria-label="Jag har läst och godkänner tävlingsvillkoren"
                            checked={termsAccepted}
                            onChange={() =>
                              termsAccepted
                                ? setTermsAccepted(false)
                                : setTermsModalOpen(true)
                            }
                            className="size-4 accent-dark-blue shrink-0 cursor-pointer !mt-[3px]"
                          />
                          <label htmlFor="terms-checkbox">
                            Jag har läst och godkänner{' '}
                            <button
                              type="button"
                              onClick={() => setTermsModalOpen(true)}
                              className="underline underline-offset-2 font-medium text-dark-blue hover:opacity-60 transition-opacity"
                            >
                              tävlingsvillkoren
                            </button>
                          </label>
                        </div>
                        <div className="flex items-start gap-3 text-sm">
                          <input
                            type="checkbox"
                            id="age-checkbox"
                            aria-label="Jag intygar att jag är 21 år eller äldre"
                            checked={ageConfirmed}
                            onChange={() => setAgeConfirmed((v) => !v)}
                            className="size-4 accent-dark-blue shrink-0 cursor-pointer !mt-[3px]"
                          />
                          <label htmlFor="age-checkbox" className="">
                            Jag intygar att jag är 21 år eller äldre
                          </label>
                        </div>
                        <p className="text-xs text-dark-blue/60 font-bold">
                          Tänk på att du endast kan skicka in ett bidrag.
                        </p>
                        <p className="text-xs text-dark-blue/60 border-t border-dark-blue/15 pt-3">
                          Kia Sweden AB behandlar de personuppgifter du lämnar
                          för att administrera tävlingen, hantera ditt
                          tävlingsbidrag och kontakta vinnare. Om du vinner kan
                          dina uppgifter och ditt tävlingsbidrag delas med
                          berörd Kia återförsäljare för att hantera vinsten,
                          utlämning av lånebil och eventuellt billåneavtal. Mer
                          information om hur Kia behandlar personuppgifter finns
                          i{' '}
                          <a
                            rel="noopener"
                            href="https://www.kia.com/se/integritetspolicy/"
                            target="_blank"
                          >
                            Kias integritetspolicy.
                          </a>
                        </p>
                        <form.Subscribe
                          selector={(state) => state.isSubmitting}
                        >
                          {(isSubmitting) => (
                            <button
                              type="submit"
                              disabled={
                                !termsAccepted || !ageConfirmed || isSubmitting
                              }
                              className={[
                                'w-full px-2 py-3 flex items-center justify-center text-white transition-opacity',
                                termsAccepted && ageConfirmed && !isSubmitting
                                  ? 'bg-black hover:underline underline-offset-2'
                                  : 'bg-black opacity-30 cursor-not-allowed',
                              ].join(' ')}
                            >
                              {isSubmitting ? (
                                <svg
                                  className="animate-spin h-5 w-5"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                  />
                                </svg>
                              ) : (
                                'Tävla!'
                              )}
                            </button>
                          )}
                        </form.Subscribe>
                        <div className="flex flex-col">
                          <form.Subscribe
                            selector={(state) =>
                              state.submissionAttempts > 0 &&
                              Object.values(state.fieldMeta).some(
                                (m) => m.errors.length > 0,
                              )
                            }
                            children={(hasErrors) =>
                              hasErrors && (
                                <p className="text-sm text-red-500 font-medium text-center">
                                  Rätta till felen i formuläret
                                </p>
                              )
                            }
                          />
                          <form.Subscribe
                            selector={(state) => state.errorMap.onServer}
                            children={(serverError) =>
                              serverError && (
                                <p className="text-sm text-red-500 font-medium text-center">
                                  {serverError as string}
                                </p>
                              )
                            }
                          />
                          <p className="text-xs font-bold text-dark-blue/70 leading-relaxed text-center">
                            Det här formuläret skyddas av reCAPTCHA.
                          </p>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

export default WowForm;
